import {
  ActionItemStatus,
  IssuesFilterByOption,
  IssuesSortByOption,
  IssueStatus,
  MetricsFilterByOption,
  SortByDirection,
  TaskItemsFilterByOption,
  TaskItemsSortByOption,
  TaskItemStatus,
  TeamIssuesGroupByOption,
  TeamOverviewSection,
  TeamTaskItemsGroupByOption,
  UserIssuesGroupByOption,
  UserStrategyFilterByOption,
  UserStrategyGroupByOption,
  UserStrategySortByOption,
  UserTaskItemsGroupByOption,
} from "../constants";

interface ISettingsData {
  homeSettings: IHomeSettings;
  sideMenuSettings: ISideMenuSettings;
  drawerSettings: IDrawerSettings;
  teamSettings: ITeamSettings;
  teamStrategySettings: {
    [planGuid: string]: ITeamStrategySettings | undefined;
  };
  teamImplementationSettings: {
    [teamSlug: string]: ITeamImplementationSettings | undefined;
  };
  teamMetricsSettings: {
    [teamSlug: string]: ITeamMetricsSettings | undefined;
  };
  userStrategySettings: {
    [userGuid: string]: IUserStrategySettings | undefined;
  };
  userImplementationSettings: {
    [userGuid: string]: IUserImplementationSettings | undefined;
  };
  searchSettings: ISearchSettings;
  dataGridSettings: {
    [dataGridId: string]: IDataGridSettings | undefined;
  };
  tableSettings: {
    [tableId: string]: ITableSettings | undefined;
  };
  featureFlags: {
    [featureFlag in FeatureFlag]: boolean;
  };
  coherenceReportSettings: ICoherenceReportSettings;
  meetingSettings: {
    [meetingGuid: string]: IMeetingSettings | undefined;
  };
}

type FeatureFlag = "surveys" | "meetings" | "chatbot" | "snowfall";

interface ITeamSettings {
  networkListView: boolean | undefined;
  expandedSection: TeamOverviewSection | undefined;
}

interface IDrawerSettings {
  width: number;
}

interface ITeamStrategySettings {
  tableView: boolean;
}

interface ITeamImplementationSettings {
  tableView: boolean;
  taskItemsGroupByOption: TeamTaskItemsGroupByOption;
  taskItemsFilterByOption: TaskItemsFilterByOption;
  taskItemsFilterByStatusOption: TaskItemStatus | undefined;
  taskItemsSortByOption: TaskItemsSortByOption;
  taskItemsSortByDirection: SortByDirection;
  issuesGroupByOption: TeamIssuesGroupByOption;
  issuesFilterByOption: IssuesFilterByOption;
  issuesFilterByStatusOption: IssueStatus | undefined;
  issuesSortByOption: IssuesSortByOption;
  issuesSortByDirection: SortByDirection;
}

interface ITeamMetricsSettings {
  filterByOption: MetricsFilterByOption;
  filterByValue: string | undefined;
}

interface IUserStrategySettings {
  tableView: boolean;
  groupByOption: UserStrategyGroupByOption;
  filterByOption: UserStrategyFilterByOption;
  filterByStatusOption: ActionItemStatus | undefined;
  sortByOption: UserStrategySortByOption;
  sortByDirection: SortByDirection;
}

interface IUserImplementationSettings {
  tableView: boolean;
  taskItemsGroupByOption: UserTaskItemsGroupByOption;
  taskItemsFilterByOption: TaskItemsFilterByOption;
  taskItemsFilterByStatusOption: TaskItemStatus | undefined;
  taskItemsSortByOption: TaskItemsSortByOption;
  taskItemsSortByDirection: SortByDirection;
  issuesGroupByOption: UserIssuesGroupByOption;
  issuesFilterByOption: IssuesFilterByOption;
  issuesFilterByStatusOption: IssueStatus | undefined;
  issuesSortByOption: IssuesSortByOption;
  issuesSortByDirection: SortByDirection;
}

interface ISideMenuSettings {
  isExpanded: boolean;
  expandedWidth: number;
}

interface IHomeSettings {
  showPeople: boolean;
  selectedTeamGuids: string[];
}

interface ISearchSettings {
  searchHistory: string[];
}

interface IDataGridSettings {
  columnVisibility: Record<string, boolean>;
}

interface ITableSettings {
  columnVisibility: Record<string, boolean>;
}

interface ICoherenceReportSettings {
  selectedTeamGuids: string[] | undefined;
  comparisonTeamGuids: string[] | undefined;
}

interface IMeetingSettings {
  filterByOption: MetricsFilterByOption;
  filterByValue: string | undefined;
}

const useLocalStorage = () => {
  function getItem<T>(key: string) {
    const serializedItem = localStorage.getItem(key);

    if (serializedItem === null) {
      return null;
    }

    try {
      return JSON.parse(serializedItem) as T;
    } catch {
      return null;
    }
  }

  function setItem<T>(key: string, item: T) {
    const serializedItem = JSON.stringify(item);
    localStorage.setItem(key, serializedItem);
  }

  function getLocalStorageSettings() {
    return getItem<ISettingsData>("settings");
  }

  function setLocalStorageSettings(settings: ISettingsData) {
    setItem("settings", settings);
  }

  return {
    getLocalStorageSettings,
    setLocalStorageSettings,
  };
};

export { useLocalStorage };

export type {
  FeatureFlag,
  ICoherenceReportSettings,
  IDataGridSettings,
  ITableSettings,
  IDrawerSettings,
  IHomeSettings,
  ISearchSettings,
  ISettingsData,
  ISideMenuSettings,
  ITeamImplementationSettings,
  ITeamMetricsSettings,
  ITeamSettings,
  ITeamStrategySettings,
  IUserImplementationSettings,
  IUserStrategySettings,
  IMeetingSettings,
};
